/**
 * BUDB PKR / Racalvin Ethereal Treasure — 5×5 grid, 25 paylines,
 * Grudge wild (joker), free spins, bonus spins + Mini/Medium/Large/Grand JP.
 *
 * Math SSOT: industry-style video slot model (target RTP / house edge).
 * @see docs/SLOTS_MATH_SSOT.md
 */
import { roundPlayGbux } from "./gbux-math";

/** Grid geometry (SSOT for client + worker) */
export const REEL_COUNT = 5;
export const ROW_COUNT = 5;
export const MAX_LINES = 25;

/**
 * Industry-standard economics for Ethereal Treasure (base game target).
 * - Online casino slots typically publish 92–97% RTP; we target mid-low band.
 * - House edge = 1 − RTP (owner profit over long run — verified by scripts/sim-rtp.mjs).
 * - Features (free/bonus) pull from base RTP budget (contribution model).
 * - Win cap applies on paid AND feature spins (notational stake = bet×lines).
 */
export const SLOT_MATH = {
  /** Theoretical long-run return to player (base + features blended) */
  targetRtp: 0.89,
  /** House edge complementary to RTP — owner edge (~11%; sim-rtp ~35k spins) */
  houseEdge: 0.11,
  /** Published band for paytable / legal-style disclosure */
  rtpBand: { min: 0.86, max: 0.93 },
  /** Volatility label for UI */
  volatility: "medium-high" as const,
  /** Max win as multiple of stake on any spin (paid or feature) */
  maxWinMultiple: 500,
  /** Big-win threshold (× total bet) */
  bigWinMultiple: 20,
  /** Mega-win threshold (× total bet) for FX tier */
  megaWinMultiple: 50,
  /** Special spin chance on paid base spin (~1.2%) */
  specialSpinChance: 0.012,
  /** Match length multipliers for 3 / 4 / 5 of a kind (L→R) */
  matchMults: [1, 2.5, 7] as const,
  /** Minimum matches for a line win */
  minMatch: 3,
  /** Wild product mult hard cap */
  wildMultCap: 4,
  /** Free-spin bank mult ceiling */
  freeMultCap: 3,
  /** Bonus-spin bank mult ceiling */
  bonusMultCap: 3,
  rules: {
    evaluation: "left-to-right on active paylines",
    scatter: "pays anywhere; does not use paylines",
    wild: "substitutes pay symbols only; not scatter/bonus/jp logos",
    jackpot:
      "3+ Most Wanted posters anywhere; paid/bonus/special only (not free voyage)",
    freeSpins: "3/4/5 Malachar minion bounty scatters → 6/10/14 free spins",
    bonus: "3/4/5 seals → 4/6/8 bonus spins (wanted-weighted strips)",
    house:
      "edge ~7% via symbol weights + feature rates + win caps; sim: scripts/sim-rtp.mjs",
  },
} as const;

export interface SlotSymbolDef {
  id: string;
  name: string;
  value: number;
  weight: number;
  /** Feature-only (no payline anchor) */
  feature?: boolean;
}

export interface WildCell {
  reel: number;
  row: number;
  multiplier: number;
}

export interface PaylineResult {
  paylineIndex: number;
  symbolId: string;
  matchCount: number;
  win: number;
  wildMultiplier: number;
  positions: Array<{ reel: number; row: number }>;
}

export type JackpotTierId = "jp_mini" | "jp_medium" | "jp_large" | "jp_grand";

export interface JackpotTier {
  id: JackpotTierId;
  name: string;
  /** Short strip label */
  shortName: string;
  /** Lore subtitle */
  title: string;
  /** Multiplier of total bet */
  mult: number;
  color: string;
  icon: string;
  /** Larger poster for overlays */
  poster?: string;
}

/**
 * Grudge's Most Wanted Rewards (replaces Mini/Medium/Large/Grand labels).
 * Fixed mults of total bet — progressive reserve not simulated (local pool style).
 */
export const JACKPOT_TIERS: Record<JackpotTierId, JackpotTier> = {
  jp_mini: {
    id: "jp_mini",
    name: "Durin",
    shortName: "Durin",
    title: "Tunnelwatcher · Deep Scout",
    mult: 6,
    color: "#94a3b8",
    icon: "/slots/bounty/durin-icon.png",
    poster: "/slots/bounty/durin-sm.jpg",
  },
  jp_medium: {
    id: "jp_medium",
    name: "Sylara",
    shortName: "Sylara",
    title: "Wildheart · Forest Spirit",
    mult: 18,
    color: "#4ade80",
    icon: "/slots/bounty/sylara-icon.png",
    poster: "/slots/bounty/sylara-sm.jpg",
  },
  jp_large: {
    id: "jp_large",
    name: "Kael",
    shortName: "Kael",
    title: "Shadowblade · 25k Gold",
    mult: 50,
    color: "#22c55e",
    icon: "/slots/bounty/kael-icon.png",
    poster: "/slots/bounty/kael-sm.jpg",
  },
  jp_grand: {
    id: "jp_grand",
    name: "Racalvin",
    shortName: "King",
    title: "Pirate King · 50k Bounty",
    mult: 150,
    color: "#fbbf24",
    icon: "/slots/bounty/racalvin-wanted-icon.png",
    poster: "/slots/bounty/racalvin-wanted-sm.jpg",
  },
};

/** Free-spin scatter lore — Lord Malachar minion bounty */
export const FREE_SPIN_BOUNTY = {
  id: "scatter" as const,
  name: "Lord Malachar",
  title: "Deathless Knight · Minion Bounty",
  note: "Seek his treasure — 3+ charts unlock free voyage spins",
  icon: "/slots/bounty/malachar-icon.png",
  poster: "/slots/bounty/malachar-sm.jpg",
} as const;

export const JACKPOT_ORDER: JackpotTierId[] = [
  "jp_mini",
  "jp_medium",
  "jp_large",
  "jp_grand",
];

export interface JackpotAward {
  tier: JackpotTierId;
  name: string;
  count: number;
  win: number;
  mult: number;
}

export interface SlotSessionState {
  freeSpinsRemaining: number;
  freeSpinsMultiplier: number;
  /** Grudge bonus-spin bank (jackpot-weighted reels) */
  bonusSpinsRemaining: number;
  bonusSpinsMultiplier: number;
  /** Pending one-shot special spin after random trigger */
  specialSpinPending: boolean;
  totalSpins: number;
  totalWagered: number;
  totalWon: number;
  bigWins: number;
  jackpots: number;
  miniHits: number;
  mediumHits: number;
  largeHits: number;
  grandHits: number;
}

export interface SpinResult {
  handId: string;
  reelGrid: string[][];
  wildCells: WildCell[];
  paylineResults: PaylineResult[];
  scatterCount: number;
  scatterPositions: Array<{ reel: number; row: number }>;
  bonusCount: number;
  bonusPositions: Array<{ reel: number; row: number }>;
  totalWin: number;
  totalBet: number;
  multiplier: number;
  isJackpot: boolean;
  isBigWin: boolean;
  freeSpinsTriggered: boolean;
  freeSpinsAwarded: number;
  freeSpinsRemaining: number;
  freeSpinsMultiplier: number;
  isFreeSpinMode: boolean;
  /** Bonus feature (jackpot special spins) */
  bonusTriggered: boolean;
  bonusSpinsAwarded: number;
  bonusSpinsRemaining: number;
  bonusSpinsMultiplier: number;
  isBonusMode: boolean;
  /** Random special spin / forced special reel set */
  isSpecialSpin: boolean;
  specialSpinTriggered: boolean;
  specialSpinsRemaining: number;
  jackpotAwards: JackpotAward[];
  jackpotWin: number;
  spinKind: "base" | "free" | "bonus" | "special";
}

/**
 * Pay symbols: lower value / higher weight = more common (house math).
 * Tuned toward ~93% blended RTP with 25-line base + features (sim-rtp).
 */
export const SLOT_SYMBOLS: SlotSymbolDef[] = [
  /* Base pays tuned so Monte Carlo ~88–92% RTP (see scripts/sim-rtp.mjs) */
  { id: "skull", name: "Skull", value: 56, weight: 2 },
  { id: "gbux", name: "GBUX", value: 34, weight: 3 },
  { id: "crown", name: "Crown", value: 22, weight: 5 },
  { id: "diamond", name: "Diamond", value: 14, weight: 8 },
  { id: "wolf", name: "Wolf", value: 9, weight: 12 },
  { id: "ace", name: "Ace", value: 6, weight: 16 },
  { id: "dice", name: "Dice", value: 4, weight: 24 },
  { id: "revolver", name: "Revolver", value: 2, weight: 34 },
  // Features rare on base — free/bonus trigger ~1/120–1/180 for 3+
  { id: "wild", name: "Grudge Wild", value: 0, weight: 4, feature: true },
  { id: "scatter", name: "Malachar Bounty", value: 0, weight: 2, feature: true },
  { id: "bonus", name: "Bonus", value: 0, weight: 2, feature: true },
  { id: "jp_mini", name: "Wanted · Durin", value: 0, weight: 1, feature: true },
  { id: "jp_medium", name: "Wanted · Sylara", value: 0, weight: 1, feature: true },
  { id: "jp_large", name: "Wanted · Kael", value: 0, weight: 1, feature: true },
  { id: "jp_grand", name: "Wanted · Racalvin", value: 0, weight: 1, feature: true },
];

const WILD_ID = "wild";
const SCATTER_ID = "scatter";
const BONUS_ID = "bonus";
const JACKPOT_LINE_ID = "skull";
/** Match mult for 3 / 4 / 5 of a kind left-to-right (from SLOT_MATH) */
const MATCH_MULTS = [...SLOT_MATH.matchMults];
const JP_IDS = new Set<string>(JACKPOT_ORDER);

/**
 * 25 fixed paylines on 5×5 (row index per reel 0–4).
 * Industry-style: horizontals + diagonals + V/W zigzags.
 */
export const PAYLINES: number[][] = [
  // 5 horizontals
  [0, 0, 0, 0, 0],
  [1, 1, 1, 1, 1],
  [2, 2, 2, 2, 2],
  [3, 3, 3, 3, 3],
  [4, 4, 4, 4, 4],
  // Main diagonals
  [0, 1, 2, 3, 4],
  [4, 3, 2, 1, 0],
  // V / inverted V
  [0, 1, 2, 1, 0],
  [4, 3, 2, 3, 4],
  [1, 2, 3, 2, 1],
  [3, 2, 1, 2, 3],
  // Steps
  [0, 0, 1, 2, 2],
  [4, 4, 3, 2, 2],
  [2, 1, 0, 1, 2],
  [2, 3, 4, 3, 2],
  // Mid bands
  [0, 1, 1, 1, 0],
  [4, 3, 3, 3, 4],
  [1, 1, 2, 3, 3],
  [3, 3, 2, 1, 1],
  // Zigzag
  [0, 1, 0, 1, 0],
  [4, 3, 4, 3, 4],
  [1, 0, 1, 2, 1],
  [3, 4, 3, 2, 3],
  // Center waves
  [2, 2, 1, 2, 2],
  [2, 2, 3, 2, 2],
];

/** Line colors for animated SVG overlays (client) */
export const PAYLINE_COLORS = [
  "#22d3ee", "#f472b6", "#fbbf24", "#a78bfa", "#4ade80",
  "#fb923c", "#38bdf8", "#e879f9", "#facc15", "#34d399",
  "#f87171", "#60a5fa", "#c084fc", "#2dd4bf", "#fde047",
  "#fb7185", "#67e8f9", "#d8b4fe", "#86efac", "#fdba74",
  "#93c5fd", "#f9a8d4", "#fcd34d", "#6ee7b7", "#c4b5fd",
] as const;

/**
 * Bonus vault strips — elevated wilds, modest wanted weight.
 * Prior pack had jp_mini=12 (≈ always 3+ Durin) which destroyed house edge.
 */
const BONUS_STRIP_WEIGHTS: Record<string, number> = {
  skull: 3,
  gbux: 4,
  crown: 5,
  diamond: 7,
  wolf: 9,
  ace: 11,
  dice: 14,
  revolver: 16,
  wild: 8,
  scatter: 1,
  bonus: 2,
  jp_mini: 2,
  jp_medium: 1,
  jp_large: 1,
  jp_grand: 1,
};

const SPECIAL_STRIP_WEIGHTS: Record<string, number> = {
  ...BONUS_STRIP_WEIGHTS,
  wild: 10,
  jp_mini: 3,
  jp_medium: 2,
  jp_large: 1,
  jp_grand: 1,
};

export function emptySlotSession(): SlotSessionState {
  return {
    freeSpinsRemaining: 0,
    freeSpinsMultiplier: 1,
    bonusSpinsRemaining: 0,
    bonusSpinsMultiplier: 1,
    specialSpinPending: false,
    totalSpins: 0,
    totalWagered: 0,
    totalWon: 0,
    bigWins: 0,
    jackpots: 0,
    miniHits: 0,
    mediumHits: 0,
    largeHits: 0,
    grandHits: 0,
  };
}

/**
 * Money-path RNG — Web Crypto only (Workers + browsers).
 * Never Math.random for reel outcomes (production integrity).
 */
function secureUnit(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0]! / 0x1_0000_0000;
}

function secureIndex(max: number): number {
  if (max <= 1) return 0;
  const limit = Math.floor(0x1_0000_0000 / max) * max;
  const buf = new Uint32Array(1);
  let x = 0;
  do {
    crypto.getRandomValues(buf);
    x = buf[0]!;
  } while (x >= limit);
  return x % max;
}

function rollWildMultiplier(): number {
  const r = secureUnit();
  if (r < 0.55) return 1;
  if (r < 0.85) return 2;
  return 3;
}

function buildPool(weights: Record<string, number> | null): string[] {
  const pool: string[] = [];
  if (weights) {
    for (const [id, w] of Object.entries(weights)) {
      for (let i = 0; i < w; i++) pool.push(id);
    }
  } else {
    for (const sym of SLOT_SYMBOLS) {
      for (let i = 0; i < sym.weight; i++) pool.push(sym.id);
    }
  }
  for (let i = pool.length - 1; i > 0; i--) {
    const j = secureIndex(i + 1);
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  return pool;
}

function spinReel(
  mode: "base" | "bonus" | "special",
): string[] {
  const weights =
    mode === "bonus"
      ? BONUS_STRIP_WEIGHTS
      : mode === "special"
        ? SPECIAL_STRIP_WEIGHTS
        : null;
  const pool = buildPool(weights);
  const strip: string[] = [];
  while (strip.length < 80) strip.push(...pool);
  const start = secureIndex(Math.max(1, strip.length - ROW_COUNT));
  return Array.from({ length: ROW_COUNT }, (_, i) => strip[start + i]!);
}

function isNonPayline(sym: string): boolean {
  return (
    sym === WILD_ID ||
    sym === SCATTER_ID ||
    sym === BONUS_ID ||
    JP_IDS.has(sym)
  );
}

function evaluatePayline(
  grid: string[][],
  wildCells: WildCell[],
  paylineIndex: number,
  betPerLine: number,
  lineMultiplier: number,
): PaylineResult | null {
  const rowPattern = PAYLINES[paylineIndex];
  const positions: Array<{ reel: number; row: number }> = [];
  const cells: string[] = [];
  for (let reel = 0; reel < REEL_COUNT; reel++) {
    const row = rowPattern[reel]!;
    cells.push(grid[reel]![row]!);
    positions.push({ reel, row });
  }

  let anchor: string | null = null;
  for (const c of cells) {
    // Payline anchors: regular symbols only (not wild / scatter / bonus / jackpots)
    if (c !== WILD_ID && c !== SCATTER_ID && c !== BONUS_ID && !JP_IDS.has(c)) {
      anchor = c;
      break;
    }
  }
  if (!anchor) return null;

  let matchCount = 0;
  let combinedWildMult = 1;
  const matchPositions: Array<{ reel: number; row: number }> = [];

  for (let reel = 0; reel < REEL_COUNT; reel++) {
    const sym = cells[reel]!;
    if (sym === anchor || sym === WILD_ID) {
      matchCount++;
      matchPositions.push(positions[reel]!);
      if (sym === WILD_ID) {
        const wc = wildCells.find(
          (w) => w.reel === reel && w.row === rowPattern[reel],
        );
        const wm = wc?.multiplier ?? 1;
        combinedWildMult = Math.min(
          combinedWildMult * wm,
          SLOT_MATH.wildMultCap,
        );
      }
    } else break;
  }

  if (matchCount < 3) return null;
  const symDef = SLOT_SYMBOLS.find((s) => s.id === anchor);
  if (!symDef || symDef.value <= 0) return null;
  const matchBonus = MATCH_MULTS[matchCount - 3] ?? MATCH_MULTS[2]!;
  const wildCap = Math.min(combinedWildMult, SLOT_MATH.wildMultCap);
  const win = Math.round(
    betPerLine * symDef.value * matchBonus * wildCap * lineMultiplier,
  );
  if (win <= 0) return null;

  return {
    paylineIndex,
    symbolId: anchor,
    matchCount,
    win,
    wildMultiplier: wildCap,
    positions: matchPositions,
  };
}

function scatterSpinsAwarded(count: number): number {
  if (count >= 5) return 10;
  if (count >= 4) return 7;
  if (count >= 3) return 5;
  return 0;
}

function bonusSpinsForCount(count: number): number {
  if (count >= 5) return 6;
  if (count >= 4) return 5;
  if (count >= 3) return 3;
  return 0;
}

function handId(): string {
  const buf = new Uint8Array(8);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
}

function countSymbol(
  grid: string[][],
  id: string,
): Array<{ reel: number; row: number }> {
  const pos: Array<{ reel: number; row: number }> = [];
  for (let reel = 0; reel < REEL_COUNT; reel++) {
    for (let row = 0; row < ROW_COUNT; row++) {
      if (grid[reel]![row] === id) pos.push({ reel, row });
    }
  }
  return pos;
}

/**
 * Collect 3+ of a jackpot logo anywhere → award that tier.
 * Allowed on paid base, bonus vault, and special only — NOT free voyage
 * (free spins already return via elevated line mults; JP there bled RTP).
 */
function evaluateJackpots(
  grid: string[][],
  notionalStake: number,
  allowJackpots: boolean,
): JackpotAward[] {
  if (!allowJackpots || notionalStake <= 0) return [];
  const awards: JackpotAward[] = [];

  for (const tierId of JACKPOT_ORDER) {
    const positions = countSymbol(grid, tierId);
    if (positions.length < 3) continue;
    const tier = JACKPOT_TIERS[tierId];
    // Stack: 3 = 1×, 4 = 1.25×, 5 = 1.5×, 7+ = 2× (tighter than old 2.5×)
    const stack =
      positions.length >= 7
        ? 2
        : positions.length >= 5
          ? 1.5
          : positions.length >= 4
            ? 1.25
            : 1;
    const win = Math.round(notionalStake * tier.mult * stack);
    awards.push({
      tier: tierId,
      name: tier.name,
      count: positions.length,
      win,
      mult: tier.mult * stack,
    });
  }
  return awards;
}

/**
 * Pure spin: mutates session in place and returns SpinResult.
 */
export function spinWithSession(
  session: SlotSessionState,
  betPerLine: number,
  lines: number,
): SpinResult {
  lines = Math.max(1, Math.min(MAX_LINES, lines));
  betPerLine = Math.max(0.1, Math.min(100, betPerLine));

  const isBonusMode = session.bonusSpinsRemaining > 0;
  const isFreeSpinMode =
    !isBonusMode && session.freeSpinsRemaining > 0;
  const isSpecialSpin =
    !isBonusMode && !isFreeSpinMode && session.specialSpinPending;

  const freeSpinsMultiplier = isFreeSpinMode
    ? session.freeSpinsMultiplier
    : 1;
  const bonusSpinsMultiplier = isBonusMode
    ? session.bonusSpinsMultiplier
    : 1;
  const lineMultiplier = isBonusMode
    ? bonusSpinsMultiplier
    : freeSpinsMultiplier;

  const paidSpin = !isFreeSpinMode && !isBonusMode && !isSpecialSpin;
  const totalBet = paidSpin ? roundPlayGbux(betPerLine * lines) : 0;

  // Consume special pending flag for this spin
  if (isSpecialSpin) session.specialSpinPending = false;

  const mode: "base" | "bonus" | "special" = isBonusMode
    ? "bonus"
    : isSpecialSpin
      ? "special"
      : "base";

  const reelGrid: string[][] = [];
  for (let r = 0; r < REEL_COUNT; r++) reelGrid.push(spinReel(mode));

  const wildCells: WildCell[] = [];
  for (let reel = 0; reel < REEL_COUNT; reel++) {
    for (let row = 0; row < ROW_COUNT; row++) {
      if (reelGrid[reel]![row] === WILD_ID) {
        wildCells.push({
          reel,
          row,
          multiplier: isBonusMode || isSpecialSpin
            ? Math.max(2, rollWildMultiplier())
            : rollWildMultiplier(),
        });
      }
    }
  }

  const activeLines =
    isFreeSpinMode || isBonusMode || isSpecialSpin
      ? MAX_LINES
      : Math.min(lines, MAX_LINES);
  const paylineResults: PaylineResult[] = [];
  for (let p = 0; p < activeLines; p++) {
    const result = evaluatePayline(
      reelGrid,
      wildCells,
      p,
      betPerLine,
      lineMultiplier,
    );
    if (result) paylineResults.push(result);
  }

  const scatterPositions = countSymbol(reelGrid, SCATTER_ID);
  const scatterCount = scatterPositions.length;
  const bonusPositions = countSymbol(reelGrid, BONUS_ID);
  const bonusCount = bonusPositions.length;

  const notionalStake = roundPlayGbux(betPerLine * lines);

  let scatterWin = 0;
  if (paidSpin && scatterCount >= 3) {
    scatterWin = Math.round(notionalStake * 0.5 * (scatterCount - 2));
  }

  // Jackpots: paid base + bonus + special only (never free voyage)
  const allowJackpots = paidSpin || isBonusMode || isSpecialSpin;
  const jackpotAwards = evaluateJackpots(reelGrid, notionalStake, allowJackpots);
  const jackpotWin = jackpotAwards.reduce((s, a) => s + a.win, 0);

  const paylineWin = paylineResults.reduce((sum, r) => sum + r.win, 0);
  let totalWin = paylineWin + scatterWin + jackpotWin;
  // Hard cap on every spin using notional stake (closes free/bonus bleed)
  if (notionalStake > 0) {
    const cap = Math.round(notionalStake * SLOT_MATH.maxWinMultiple);
    if (totalWin > cap) totalWin = cap;
  }

  // ── Free spins (scatter) ────────────────────────────────────────────────
  let freeSpinsTriggered = false;
  let freeSpinsAwarded = 0;
  if (scatterCount >= 3 && !isBonusMode) {
    freeSpinsAwarded = scatterSpinsAwarded(scatterCount);
    if (isFreeSpinMode) {
      session.freeSpinsRemaining += freeSpinsAwarded;
      session.freeSpinsMultiplier = Math.min(
        session.freeSpinsMultiplier + 1,
        SLOT_MATH.freeMultCap,
      );
    } else {
      session.freeSpinsRemaining += freeSpinsAwarded;
      if (session.freeSpinsRemaining === freeSpinsAwarded) {
        session.freeSpinsMultiplier =
          scatterCount >= 5 ? 3 : scatterCount >= 4 ? 2 : 2;
      }
    }
    freeSpinsTriggered = true;
  }

  // ── Bonus spins (3+ bonus / grudge feature) ─────────────────────────────
  let bonusTriggered = false;
  let bonusSpinsAwarded = 0;
  if (bonusCount >= 3) {
    bonusSpinsAwarded = bonusSpinsForCount(bonusCount);
    if (isBonusMode) {
      session.bonusSpinsRemaining += bonusSpinsAwarded;
      session.bonusSpinsMultiplier = Math.min(
        session.bonusSpinsMultiplier + 1,
        SLOT_MATH.bonusMultCap,
      );
    } else {
      session.bonusSpinsRemaining += bonusSpinsAwarded;
      if (session.bonusSpinsRemaining === bonusSpinsAwarded) {
        session.bonusSpinsMultiplier = 2;
      }
    }
    bonusTriggered = true;
  }

  // ── Random special spin trigger on paid spins (~4%) ─────────────────────
  let specialSpinTriggered = false;
  if (
    paidSpin &&
    !session.specialSpinPending &&
    secureUnit() < SLOT_MATH.specialSpinChance
  ) {
    session.specialSpinPending = true;
    specialSpinTriggered = true;
  }

  // Consume one free / bonus spin after resolving awards
  if (isBonusMode) {
    session.bonusSpinsRemaining = Math.max(0, session.bonusSpinsRemaining - 1);
  } else if (isFreeSpinMode) {
    session.freeSpinsRemaining = Math.max(0, session.freeSpinsRemaining - 1);
  }

  const isLineJackpot = paylineResults.some(
    (r) => r.symbolId === JACKPOT_LINE_ID && r.matchCount === 5,
  );
  const isJackpot = isLineJackpot || jackpotAwards.length > 0;
  const multiplierRatio =
    totalBet > 0 ? Math.round((totalWin / totalBet) * 10) / 10 : 0;
  const isBigWin =
    paidSpin &&
    totalBet > 0 &&
    (multiplierRatio >= SLOT_MATH.bigWinMultiple || jackpotWin > 0);

  session.totalSpins += 1;
  session.totalWagered += totalBet;
  session.totalWon += totalWin;
  if (isBigWin) session.bigWins += 1;
  if (isJackpot) session.jackpots += 1;
  for (const a of jackpotAwards) {
    if (a.tier === "jp_mini") session.miniHits += 1;
    if (a.tier === "jp_medium") session.mediumHits += 1;
    if (a.tier === "jp_large") session.largeHits += 1;
    if (a.tier === "jp_grand") session.grandHits += 1;
  }

  const spinKind: SpinResult["spinKind"] = isBonusMode
    ? "bonus"
    : isSpecialSpin
      ? "special"
      : isFreeSpinMode
        ? "free"
        : "base";

  return {
    handId: handId(),
    reelGrid,
    wildCells,
    paylineResults,
    scatterCount,
    scatterPositions,
    bonusCount,
    bonusPositions,
    totalWin,
    totalBet,
    multiplier: multiplierRatio,
    isJackpot,
    isBigWin,
    freeSpinsTriggered,
    freeSpinsAwarded,
    freeSpinsRemaining: session.freeSpinsRemaining,
    freeSpinsMultiplier: isFreeSpinMode
      ? freeSpinsMultiplier
      : session.freeSpinsMultiplier,
    isFreeSpinMode,
    bonusTriggered,
    bonusSpinsAwarded,
    bonusSpinsRemaining: session.bonusSpinsRemaining,
    bonusSpinsMultiplier: isBonusMode
      ? bonusSpinsMultiplier
      : session.bonusSpinsMultiplier,
    isBonusMode,
    isSpecialSpin,
    specialSpinTriggered,
    specialSpinsRemaining: session.specialSpinPending ? 1 : 0,
    jackpotAwards,
    jackpotWin,
    spinKind,
  };
}

export function getPaytable() {
  const [m3, m4, m5] = SLOT_MATH.matchMults;
  return SLOT_SYMBOLS.filter((s) => !s.feature).map((s) => ({
    id: s.id,
    name: s.name,
    pays3: Math.round(s.value * m3),
    pays4: Math.round(s.value * m4),
    pays5: Math.round(s.value * m5),
  }));
}

export function getFeaturePaytable() {
  return {
    grid: { reels: REEL_COUNT, rows: ROW_COUNT, maxLines: MAX_LINES },
    math: {
      targetRtp: SLOT_MATH.targetRtp,
      houseEdge: SLOT_MATH.houseEdge,
      rtpBand: SLOT_MATH.rtpBand,
      volatility: SLOT_MATH.volatility,
      maxWinMultiple: SLOT_MATH.maxWinMultiple,
      rules: SLOT_MATH.rules,
    },
    wild: {
      id: "wild",
      name: "Racalvin Wild (Joker)",
      icon: "/slots/icons/v5/wild.jpg",
      note: `Substitutes pay symbols. Mult up to ${SLOT_MATH.wildMultCap}× product cap.`,
    },
    scatter: {
      id: FREE_SPIN_BOUNTY.id,
      name: FREE_SPIN_BOUNTY.name,
      title: FREE_SPIN_BOUNTY.title,
      icon: FREE_SPIN_BOUNTY.icon,
      poster: FREE_SPIN_BOUNTY.poster,
      note: FREE_SPIN_BOUNTY.note + " · 3/4/5 → 6/10/14 free spins.",
    },
    bonus: {
      id: "bonus",
      name: "Ethereal Seal Bonus",
      icon: "/slots/icons/v5/bonus.jpg",
      note: "3+ anywhere = 4/6/8 Bonus Spins with modest Most Wanted weight.",
    },
    mostWanted: JACKPOT_ORDER.map((id) => ({
      ...JACKPOT_TIERS[id],
      note: `3+ ${JACKPOT_TIERS[id].name} posters pays ${JACKPOT_TIERS[id].mult}× total bet (4=1.25×, 5=1.5×, 7+=2×). Paid/bonus/special only.`,
    })),
    jackpots: JACKPOT_ORDER.map((id) => ({
      ...JACKPOT_TIERS[id],
      note: `3+ ${JACKPOT_TIERS[id].name} posters pays ${JACKPOT_TIERS[id].mult}× total bet (4=1.25×, 5=1.5×, 7+=2×). Paid/bonus/special only.`,
    })),
    paylines: PAYLINES.length,
  };
}

/** Public math disclosure for UI / paytable */
export function getSlotMathDisclosure() {
  return {
    ...SLOT_MATH,
    title: "Ethereal Treasure",
    reels: REEL_COUNT,
    rows: ROW_COUNT,
    lines: MAX_LINES,
    evaluation: "left-to-right",
  };
}
